'use client'

import * as PopoverPrimitive from '@radix-ui/react-popover'
import { Check, Plus, Search, Settings2, X } from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'
import { useRevealOnScroll } from '../hooks/use-reveal-on-scroll'
import { REVEAL_CHUNK } from '../reveal'
import { isBodyScrollLocked } from './popover'

/**
 * One primitive for every "search through a list and pick some things" filter
 * surface: assignee pickers, status selectors, custom-field SINGLE_SELECT /
 * MULTI_SELECT. Replaces the older AdaptiveMultiSelectFilter + tasker's
 * UserSearchPicker.
 *
 * Behavior contract:
 *  - Focus the input, before typing → dropdown shows EVERY option, revealed
 *    a chunk at a time: `initialSuggestionCount` rows to start (default 10),
 *    then another `REVEAL_CHUNK` each time the sentinel at the bottom of the
 *    scroll region comes into view. Nothing is truncated — an option the
 *    caller passed is always reachable by scrolling.
 *  - Type → local filter by `label` / `searchText`. If `onRemoteSearch` is
 *    supplied, also debounces a remote call (300ms) and merges results
 *    (local wins on value collision).
 *  - Selected values render as chips above the search input (multi) or as
 *    the only visible state (single). × removes.
 *  - Single-select dropdown closes on pick. Multi-select stays open.
 *
 * Positioning: the dropdown is a Radix Popover portaled to <body>, so it
 * floats above any host scroll/overflow container (filter panels, sheets,
 * dialogs). Never wrap this primitive in your own absolute positioner —
 * the host should compose it inline like a normal form field.
 */

export interface SearchableSelectOption {
  value: string
  label: string
  /** Optional extra text indexed for local search (e.g. email). Not shown in the label. */
  searchText?: string
  /** Render the row non-interactive (e.g. "already a customer"). Shown, not pickable. */
  disabled?: boolean
  /**
   * Optional hex color (e.g. `#3b82f6`) for the option. Renders a color dot in
   * the dropdown row and tints the multi-select chip (12.5%-alpha background +
   * full-strength text), matching the tag/status pill treatment so chips stay
   * readable in both light and dark themes.
   */
  colorHex?: string
  /**
   * Optional leading icon for the dropdown row (e.g. a muted 16px lucide
   * glyph on a pinned "Unassigned" row). Not rendered in chips.
   */
  icon?: React.ReactNode
  /**
   * Optional muted second line under the label in the dropdown row (e.g. a
   * menu's deadline summary). Also indexed for local search.
   */
  description?: string
}

export interface SearchableSelectCreateAction {
  /** Row label for the pinned create affordance, e.g. (q) => `Create "${q}"…`. */
  label: (query: string) => string
  onCreate: (query: string) => void
}

export interface SearchableSelectManageAction {
  /** Row label for the pinned manage affordance, e.g. "Manage objectives…". */
  label: string
  /** Called when picked (dropdown closes first) — typically opens a ManagedOptionListDialog. */
  onManage: () => void
}

interface CommonProps {
  label?: string
  placeholder?: string
  options: SearchableSelectOption[]
  onRemoteSearch?: (query: string) => Promise<SearchableSelectOption[]>
  /**
   * How many rows to render before the first scroll. NOT a cap — the rest
   * are revealed a chunk at a time as the user scrolls. Defaults to
   * `DEFAULT_INITIAL`.
   */
  initialSuggestionCount?: number
  className?: string
  /**
   * Pinned bottom row offering to create the typed value when nothing matches
   * it exactly. Renders only while the query is non-empty; picking it calls
   * `onCreate(query)` and closes the dropdown.
   */
  createAction?: SearchableSelectCreateAction
  /**
   * Pinned bottom row opening the catalog manager (rename/archive) for the
   * option list backing this picker. Always visible while the dropdown is
   * open; gate it caller-side (e.g. admins only).
   */
  manageAction?: SearchableSelectManageAction
  /**
   * Options pinned to the TOP of the dropdown, above the query-filtered
   * results: exempt from query filtering (always visible while the dropdown
   * is open — even when a query matches nothing), deduped from local/remote
   * results, and included in chip label resolution. Use for synthetic
   * choices like an "Unassigned" sentinel that no remote search will ever
   * return.
   */
  pinnedOptions?: SearchableSelectOption[]
  /**
   * Extra value → label entries used when rendering chips/pills for values
   * that appear in NO option list (e.g. a sentinel persisted in a saved
   * filter config on a host that doesn't offer the pinned option). Highest
   * precedence in label resolution.
   */
  labelOverrides?: Record<string, string>
  /**
   * OPT-IN: when a single selection exists and its option has a `colorHex`,
   * render an 8px color dot inside the trigger's left padding, before the
   * selected label. Hidden while the user types a query. Default false —
   * consumers that don't pass this render exactly as before.
   */
  showTriggerColorDot?: boolean
}

export interface SearchableMultiSelectProps extends CommonProps {
  multi: true
  value: string[]
  onChange: (next: string[]) => void
}

export interface SearchableSingleSelectProps extends CommonProps {
  multi?: false
  value: string | null
  onChange: (next: string | null) => void
}

export type SearchableSelectProps = SearchableMultiSelectProps | SearchableSingleSelectProps

const DEFAULT_INITIAL = REVEAL_CHUNK
const REMOTE_DEBOUNCE_MS = 300

/**
 * Pure composition of the scrollable (non-pinned) dropdown list: local
 * options filtered by the query, merged with remote results (local wins on
 * value collision), and deduped against `pinnedOptions` — pinned rows render
 * in their own group above this list and are exempt from query filtering,
 * so they must never appear here too. Exported for unit tests.
 */
export function composeDropdownOptions({
  options,
  remoteResults,
  pinnedOptions,
  query,
}: {
  options: SearchableSelectOption[]
  remoteResults: SearchableSelectOption[]
  pinnedOptions?: SearchableSelectOption[]
  query: string
}): SearchableSelectOption[] {
  const needle = query.trim().toLowerCase()
  const localMatches = needle
    ? options.filter((o) => {
        const hay = (o.label + ' ' + (o.searchText ?? '') + ' ' + (o.description ?? '')).toLowerCase()
        return hay.includes(needle)
      })
    : options
  const seen = new Set((pinnedOptions ?? []).map((o) => o.value))
  const merged: SearchableSelectOption[] = []
  for (const o of localMatches) {
    if (!seen.has(o.value)) {
      merged.push(o)
      seen.add(o.value)
    }
  }
  for (const r of remoteResults) {
    if (!seen.has(r.value)) {
      merged.push(r)
      seen.add(r.value)
    }
  }
  return merged
}

export function SearchableSelect(props: SearchableSelectProps) {
  const {
    label,
    placeholder = 'Search…',
    options,
    onRemoteSearch,
    initialSuggestionCount = DEFAULT_INITIAL,
    className,
    createAction,
    manageAction,
    pinnedOptions,
    labelOverrides,
    showTriggerColorDot = false,
  } = props

  const [query, setQuery] = React.useState('')
  const [open, setOpen] = React.useState(false)
  const [remoteResults, setRemoteResults] = React.useState<SearchableSelectOption[]>([])
  const [labelCache, setLabelCache] = React.useState<Record<string, string>>({})
  const inputRef = React.useRef<HTMLInputElement>(null)

  const selectedIds = React.useMemo<string[]>(() => {
    if (props.multi) {
      return props.value
    }
    return props.value ? [props.value] : []
  }, [props.multi, props.value])

  const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds])

  // Keep a label cache so chips render correctly for values not in `options`
  // (e.g. a remote-searched assignee the server found but `knownUsers` didn't).
  React.useEffect(() => {
    const next: Record<string, string> = {}
    for (const opt of pinnedOptions ?? []) {
      next[opt.value] = opt.label
    }
    for (const opt of options) {
      if (!next[opt.value]) {
        next[opt.value] = opt.label
      }
    }
    for (const opt of remoteResults) {
      if (!next[opt.value]) {
        next[opt.value] = opt.label
      }
    }
    if (Object.keys(next).length > 0) {
      setLabelCache((prev) => ({ ...prev, ...next }))
    }
  }, [options, remoteResults, pinnedOptions])

  const colorByValue = React.useMemo<Record<string, string>>(() => {
    const next: Record<string, string> = {}
    for (const opt of options) {
      if (opt.colorHex) {
        next[opt.value] = opt.colorHex
      }
    }
    for (const opt of remoteResults) {
      if (opt.colorHex && !next[opt.value]) {
        next[opt.value] = opt.colorHex
      }
    }
    return next
  }, [options, remoteResults])

  // Debounced remote search.
  React.useEffect(() => {
    if (!onRemoteSearch) {
      return
    }
    const q = query.trim()
    if (!q) {
      setRemoteResults([])
      return
    }
    let cancelled = false
    const timer = setTimeout(() => {
      onRemoteSearch(q)
        .then((r) => {
          if (!cancelled) {
            setRemoteResults(r)
          }
        })
        .catch(() => {
          if (!cancelled) {
            setRemoteResults([])
          }
        })
    }, REMOTE_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query, onRemoteSearch])

  // Compose the dropdown list: local filter + remote results, deduped by value
  // and against the pinned group (a pinned option must never render twice).
  // No truncation here — `visibleCount` below controls how much of this is
  // rendered, and it grows as the user scrolls.
  const dropdownOptions = React.useMemo<SearchableSelectOption[]>(
    () => composeDropdownOptions({ options, remoteResults, pinnedOptions, query }),
    [options, query, remoteResults, pinnedOptions],
  )

  // ── Scroll-to-reveal ────────────────────────────────────────────
  // Render `initialSuggestionCount` rows (the default REVEAL_CHUNK is sized
  // to overflow the 24rem cap, so a scrollbar shows immediately), then
  // reveal more via the shared hook: sentinel observer + scroll backstop +
  // idle background top-up. See `hooks/use-reveal-on-scroll.ts`.
  const total = dropdownOptions.length
  const { scrollRegionRef, sentinelRef, visibleCount, hasMore } = useRevealOnScroll({
    total,
    resetKey: `${open}|${query}`,
    initial: initialSuggestionCount,
    enabled: open,
  })
  const visibleOptions = React.useMemo(() => dropdownOptions.slice(0, visibleCount), [dropdownOptions, visibleCount])

  const resolveLabel = (id: string): string =>
    labelOverrides?.[id] ??
    labelCache[id] ??
    pinnedOptions?.find((o) => o.value === id)?.label ??
    options.find((o) => o.value === id)?.label ??
    id

  const pick = (value: string) => {
    if (props.multi) {
      if (props.value.includes(value)) {
        props.onChange(props.value.filter((v) => v !== value))
      } else {
        props.onChange([...props.value, value])
      }
      setQuery('')
      inputRef.current?.focus()
    } else {
      props.onChange(props.value === value ? null : value)
      setQuery('')
      setOpen(false)
    }
  }

  const clearSelected = (value: string) => {
    if (props.multi) {
      props.onChange(props.value.filter((v) => v !== value))
    } else {
      props.onChange(null)
    }
  }

  // Single-select: if selected and not focused, show a pill with the label
  // instead of the empty search input so the current choice reads clearly.
  const singleSelectedLabel = !props.multi && props.value ? resolveLabel(props.value) : null

  // Opt-in collapsed-trigger color dot (single-select only): shown next to
  // the selected label, hidden while the user types a query.
  const triggerDotColor =
    showTriggerColorDot && !props.multi && props.value && !query ? colorByValue[props.value] : undefined

  // Create affordance: only while a query is typed, and never when an option
  // already matches the typed text exactly (that pick should win).
  const trimmedQuery = query.trim()
  const showCreateRow = Boolean(
    createAction &&
    trimmedQuery &&
    !dropdownOptions.some((opt) => opt.label.toLowerCase() === trimmedQuery.toLowerCase()),
  )

  const create = () => {
    if (!createAction || !trimmedQuery) {
      return
    }
    createAction.onCreate(trimmedQuery)
    setQuery('')
    setOpen(false)
  }

  // One row renderer for pinned + filtered options so selection affordances
  // (checkbox / check mark, color dot, disabled state) stay identical.
  const renderOptionRow = (opt: SearchableSelectOption) => {
    const checked = selectedSet.has(opt.value)
    return (
      <button
        key={opt.value}
        type="button"
        disabled={opt.disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => pick(opt.value)}
        className={cn(
          'flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-muted transition-colors text-left',
          checked && !props.multi && 'bg-primary/5',
          opt.disabled && 'opacity-50 cursor-not-allowed hover:bg-transparent',
        )}>
        {props.multi ? (
          <span
            className={cn(
              'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
              checked ? 'bg-primary border-primary' : 'border-input',
            )}>
            {checked ? <Check className="w-3 h-3 text-primary-foreground" strokeWidth={3} /> : null}
          </span>
        ) : (
          <Check className={cn('h-3.5 w-3.5 shrink-0 text-primary', checked ? 'opacity-100' : 'opacity-0')} />
        )}
        {opt.icon ? <span className="shrink-0 text-muted-foreground [&>svg]:h-4 [&>svg]:w-4">{opt.icon}</span> : null}
        {opt.colorHex ? (
          <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: opt.colorHex }} />
        ) : null}
        {opt.description ? (
          <span className="min-w-0 flex-1">
            <span className="block truncate">{opt.label}</span>
            <span className="block truncate text-xs text-muted-foreground">{opt.description}</span>
          </span>
        ) : (
          <span className="truncate">{opt.label}</span>
        )}
        {opt.searchText ? (
          <span className="ml-auto text-muted-foreground truncate text-xs">{opt.searchText}</span>
        ) : null}
      </button>
    )
  }

  return (
    <div className={className}>
      {label ? (
        <label className="block text-sm md:text-xs text-muted-foreground mb-1.5 md:mb-1">
          {label}
          {props.multi && selectedIds.length > 0 ? (
            <span className="ml-1 text-primary">({selectedIds.length})</span>
          ) : null}
        </label>
      ) : null}

      {/* Chip row for multi-select with picks */}
      {props.multi && selectedIds.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedIds.map((id) => {
            const chipColor = colorByValue[id]
            return (
              <span
                key={id}
                className={cn(
                  'inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md',
                  !chipColor && 'bg-primary/10 text-primary',
                )}
                style={chipColor ? { backgroundColor: `${chipColor}20`, color: chipColor } : undefined}>
                <span className="truncate max-w-[160px]">{resolveLabel(id)}</span>
                <button
                  type="button"
                  onClick={() => clearSelected(id)}
                  aria-label={`Remove ${resolveLabel(id)}`}
                  className={chipColor ? 'opacity-80 hover:opacity-100' : 'hover:text-primary/70'}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            )
          })}
        </div>
      ) : null}

      {/* Search input + portaled popover. Anchor lets the popover position
          relative to the input regardless of overflow ancestors. */}
      <PopoverPrimitive.Root modal={open && isBodyScrollLocked()} open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Anchor asChild>
          <div data-ordo-ssel-anchor="" className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            {triggerDotColor ? (
              <span
                aria-hidden
                className="absolute left-9 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full"
                style={{ backgroundColor: triggerDotColor }}
              />
            ) : null}
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                if (!open) {
                  setOpen(true)
                }
              }}
              // Open on pointerdown so the inner layer registers with Radix
              // before the parent popover's outside-click check runs.
              onPointerDown={() => setOpen(true)}
              onFocus={() => setOpen(true)}
              placeholder={singleSelectedLabel ?? placeholder}
              // Sizing matches the Input primitive: h-9 + text-base on mobile
              // (avoids iOS focus-zoom; the threshold is 16px) collapsing to
              // text-sm on desktop. focus-visible (not focus) so click-focus
              // doesn't paint a ring that gets clipped by overflow ancestors.
              className={cn(
                'flex h-9 w-full rounded-md border border-input bg-transparent pl-9 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
                // 36px (dot left offset) + 8px dot + 8px gap to the label.
                triggerDotColor && 'pl-[52px]',
                singleSelectedLabel && !query && !open ? 'pr-9 text-foreground placeholder:text-foreground' : 'pr-3',
              )}
            />
            {singleSelectedLabel && !query && !open ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  clearSelected(selectedIds[0])
                }}
                aria-label="Clear selection"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </PopoverPrimitive.Anchor>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            sideOffset={4}
            collisionPadding={8}
            // Keep keyboard focus on the input — the popover is a passive
            // listbox, not a focus target.
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            // Clicks / focus on the anchor (our own input + chip row) must
            // not dismiss this content. Radix's Anchor→Content coupling
            // doesn't always cover programmatic focus from onPointerDown.
            onPointerDownOutside={(e) => {
              if ((e.target as HTMLElement | null)?.closest('[data-ordo-ssel-anchor]')) {
                e.preventDefault()
              }
            }}
            onFocusOutside={(e) => {
              if ((e.target as HTMLElement | null)?.closest('[data-ordo-ssel-anchor]')) {
                e.preventDefault()
              }
            }}
            style={{
              // At least as wide as the trigger, but allowed to grow so long
              // option labels (e.g. IANA timezone ids in a narrow rail field)
              // stay readable instead of truncating.
              width: 'max-content',
              maxWidth: 'min(24rem, var(--radix-popper-available-width))',
              minWidth: 'var(--radix-popper-anchor-width)',
            }}
            // 24rem cap (~14 rows) instead of 16rem so the cap doesn't
            // sit *exactly* at the row count people expect to see —
            // makes the "there is more, scroll for it" affordance
            // unmistakable. The Content is a bounded flex column: the
            // result list (below) scrolls internally while the create
            // row stays pinned as a footer so it can never be clipped.
            className="z-modal flex flex-col max-h-[min(var(--radix-popover-content-available-height),24rem)] max-w-[calc(100vw-16px)] overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-lg ring-1 ring-black/5 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
            {/* Pinned group — above the scroll region so it stays visible
                regardless of query or scroll position, with a thin divider
                separating it from the searchable list (or its empty state). */}
            {pinnedOptions && pinnedOptions.length > 0 ? (
              <div className="shrink-0 border-b border-border">{pinnedOptions.map(renderOptionRow)}</div>
            ) : null}
            {/* Scroll region for results. `[scrollbar-width:thin]` paints a
                subtle bar on macOS/Chromium so the "more, scroll for it" cue
                isn't invisible until the user starts scrolling. */}
            <div
              ref={scrollRegionRef}
              className="flex-1 min-h-0 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:var(--border)_transparent]">
              {visibleOptions.length > 0 ? (
                visibleOptions.map(renderOptionRow)
              ) : showCreateRow ? null : (
                <p className="text-xs text-muted-foreground py-3 px-2.5 text-center">No matches</p>
              )}
              {/* Reveal sentinel — `rootMargin` fires it slightly before it
                  scrolls into view so the next chunk is already there. */}
              {hasMore ? <div ref={sentinelRef} aria-hidden className="h-px w-full shrink-0" /> : null}
            </div>
            {showCreateRow ? (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={create}
                className={cn(
                  'flex shrink-0 items-center gap-2 w-full px-3 py-2 text-sm hover:bg-muted transition-colors text-left text-primary',
                  dropdownOptions.length > 0 && 'border-t border-border',
                )}>
                <Plus className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{createAction?.label(trimmedQuery)}</span>
              </button>
            ) : null}
            {manageAction ? (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setOpen(false)
                  manageAction.onManage()
                }}
                className={cn(
                  'flex shrink-0 items-center gap-2 w-full px-3 py-2 text-sm hover:bg-muted transition-colors text-left text-muted-foreground',
                  (dropdownOptions.length > 0 || showCreateRow) && 'border-t border-border',
                )}>
                <Settings2 className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{manageAction.label}</span>
              </button>
            ) : null}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </div>
  )
}
